import React, { useMemo, useState } from 'react';
import AppChart, { ChartSeries } from 'shared/components/Chart/AppChart';
import AppIcon from 'shared/components/AppIcon';
import { Skeleton } from 'primereact/skeleton';
import ReportDataTable, {
  ReportColumn,
} from 'shared/components/Table/ReportDataTable';
import {
  infraTypes,
  infraDetails,
  infraComplianceByPeriod,
  buildComplianceSeries,
  infraOverview,
  getInfraDistricts,
  InfraDetail,
  InfraOverviewRow,
} from 'shared/json/infrastructureData';
import {
  Filters,
  EMPTY_FILTERS,
  drillHint,
  drillLevelLabel,
  drillSearchPlaceholder,
  scopeLabel,
} from 'shared/json/drilldown';
import DrillBreadcrumb from 'shared/components/Dashboard/DrillBreadcrumb';
import InfrastructureComparison from './InfrastructureComparison';
// import InfrastructureAlarming from './InfrastructureAlarming';
import InfrastructureCaution from './InfrastructureCaution';
import {
  useGetSchoolInfraSummaryQuery,
  useGetInfraAcdWiseSummaryQuery,
  InfraAcdWiseItem,
  useGetSchoolInfraTypeDistrictsQuery,
  useGetCommonMastersQuery,
  useGetStudentChildCountAllYearQuery,
  SchoolInfraSummaryResult,
  SchoolInfraSummaryOverviewRow,
  SchoolInfraSummaryYear,
} from './Dashboard.service';

import './Infrastructure.scss';

// UI sidebar key -> API numeric type id.
const UI_TYPE_ID: Record<string, number> = {
  classrooms: 1,
  toilets: 2,
  urinals: 3,
  'drinking-water': 4,
  labs: 5,
  furniture: 6,
};
// API overview key -> UI sidebar key.
const API_KEY_TO_UI: Record<string, string> = {
  classrooms: 'classrooms',
  toilets: 'toilets',
  urinals: 'urinals',
  drinking_water: 'drinking-water',
  labs: 'labs',
  furniture: 'furniture',
};
const iNum = (v: unknown) => Number(v) || 0;
const iFmt = (v: unknown) => (Number(v) || 0).toLocaleString('en-IN');
const iPct = (v: unknown) => `${Number(v) || 0}%`;

// Map one schoolinfrasummary result onto the InfraDetail shape the UI renders.
function buildDetailFromSummary(r: SchoolInfraSummaryResult): InfraDetail {
  const expected = iNum(r.counts.schools_expected);
  const submitted = iNum(r.counts.schools_submitted);
  const pending = iNum(r.counts.entry_pending);
  const good = iNum(r.condition.good);
  const repair = iNum(r.condition.need_repair);
  // const demolish = iNum(r.condition.to_be_demolished || 0); // hidden for now
  return {
    kpis: [
      {
        value: iPct(r.kpis.data_entry_compliance_pct),
        label: 'DATA ENTRY COMPLIANCE',
        icon: 'fa-solid fa-clipboard-check',
        tone: 'primary',
      },
      {
        value: iPct(r.kpis.availability_vs_norms_pct),
        label: 'AVAILABILITY VS NORMS',
        icon: 'fa-solid fa-scale-balanced',
        tone: 'info',
      },
      {
        value: iPct(r.kpis.good_condition_pct),
        label: 'GOOD CONDITION',
        icon: 'fa-solid fa-circle-check',
        tone: 'success',
      },
      {
        value: iPct(r.kpis.shortfall_gap_pct),
        label: 'SHORTFALL GAP',
        icon: 'fa-solid fa-triangle-exclamation',
        tone: 'warning',
      },
    ],
    stats: [
      { value: iFmt(expected), label: 'SCHOOLS EXPECTED' },
      { value: iFmt(submitted), label: 'SCHOOLS SUBMITTED' },
      { value: iFmt(pending), label: 'ENTRY PENDING' },
      { value: iFmt(r.counts.available_units), label: 'AVAILABLE UNITS' },
      { value: iFmt(r.counts.required_by_norms), label: 'REQUIRED BY NORMS' },
      { value: iFmt(r.counts.shortfall), label: 'SHORTFALL' },
    ],
    dataEntry: [
      {
        label: 'Submitted',
        value: submitted,
        count: submitted,
        color: '#4a9e5c',
      },
      { label: 'Pending', value: pending, count: pending, color: '#d9584f' },
    ],
    dataEntrySubtitle: `${iFmt(submitted)} of ${iFmt(expected)} schools`,
    vsNorms: [
      {
        label: 'Met',
        value: iNum(r.vs_norms.met_pct),
        color: '#3b82f6',
        alwaysLabel: true,
      },
      {
        label: 'Shortfall',
        value: iNum(r.vs_norms.shortfall_pct),
        color: '#d9584f',
        alwaysLabel: true,
      },
    ],
    condition: [
      { label: 'Good condition', value: good, count: good, color: '#2e8b48' },
      { label: 'Needs repair', value: repair, count: repair, color: '#f0a835' },
      // "To be demolished" slice hidden for now — revert if it's needed again.
      // {
      //   label: 'To be demolished',
      //   value: demolish ? demolish : 0,
      //   count: demolish,
      //   color: '#d9584f',
      // },
    ],
  };
}

/** Zero-state used only when a selected school's scoped response is absent. */
function emptyDetail(template: InfraDetail): InfraDetail {
  return {
    ...template,
    kpis: template.kpis.map((kpi) => ({ ...kpi, value: '0%' })),
    stats: template.stats.map((stat) => ({ ...stat, value: '0' })),
    dataEntry: template.dataEntry.map((slice) => ({
      ...slice,
      value: 0,
      count: 0,
    })),
    dataEntrySubtitle: '0 of 1 schools',
    vsNorms: template.vsNorms.map((slice) => ({ ...slice, value: 0 })),
    condition: template.condition.map((slice) => ({
      ...slice,
      value: 0,
      count: 0,
    })),
  };
}

function buildOverviewFromSummary(
  rows: SchoolInfraSummaryOverviewRow[]
): InfraOverviewRow[] {
  return rows.map((o) => {
    const uiKey = API_KEY_TO_UI[o.key] ?? o.key;
    const meta = infraTypes.find((t) => t.key === uiKey);
    const available = iNum(o.available);
    const norms = iNum(o.required);
    return {
      key: uiKey,
      type: o.name,
      icon: meta?.icon ?? 'fa-solid fa-chair',
      available,
      norms,
      shortfall: iNum(o.shortfall),
      surplus: iNum(o.surplus),
      availability: iPct(o.availability_pct),
      coverage: iPct(o.coverage_pct),
      good: iPct(o.good_pct),
    };
  });
}

// Stacked-bar palette for the Data Entry Compliance chart.
const SUBMITTED_COLOR = '#2e8b48';
const PENDING_COLOR = '#c0392b';

/**
 * One colour per bar, with every bar except the LAST faded.
 *
 * The latest academic year is the one that matters, so it renders at full
 * strength while history sits back. Done with per-bar colours rather than
 * AppChart's `highlightLabel`, because that replaces the colour of every
 * series at the highlighted index — which on a stacked bar would paint both
 * Submitted and Pending the same shade and destroy the split.
 */
const fadeExceptLast = (hex: string, count: number): string[] => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  const faded = m
    ? `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, 0.45)`
    : hex;
  return Array.from({ length: count }, (_, i) =>
    i === count - 1 ? hex : faded
  );
};

// Academic-year-wise compliance built from infraAcdWiseSummary.
//
// That endpoint returns only `items[].total` — the schools that SUBMITTED an
// entry in each academic year. Expected comes from schoolinfrasummary's
// `counts.schools_expected` (the same figure as the SCHOOLS EXPECTED stat
// tile), and the rest is derived:
//   pending       = expected - submitted   (floored at 0)
//   compliance %  = submitted / expected * 100
function buildComplianceFromAcdWise(
  items: InfraAcdWiseItem[],
  expected: number
) {
  const labels = items.map((y) => y.academic_year);
  const submitted = items.map((y) => iNum(y.total));
  // Expected is a point-in-time school count, so it applies to every year.
  // Guard the subtraction: a year can report more submissions than the
  // current expected roll, which would otherwise show negative pending.
  const pending = submitted.map((s) => Math.max(0, expected - s));
  const compliance = submitted.map((s) =>
    expected > 0 ? Math.round((s / expected) * 1000) / 10 : 0
  );
  const series: ChartSeries[] = [
    {
      label: 'Submitted',
      data: submitted,
      color: SUBMITTED_COLOR,
      // Stacked bars: Submitted sits under Pending so the two together reach
      // Expected. Every year except the latest is faded, which reads as "this
      // is the current year" without recolouring it (recolouring would lose
      // the green/red split that carries the meaning).
      barColors: fadeExceptLast(SUBMITTED_COLOR, labels.length),
    },
    {
      label: 'Pending',
      data: pending,
      color: PENDING_COLOR,
      barColors: fadeExceptLast(PENDING_COLOR, labels.length),
    },
    {
      label: 'Compliance %',
      data: compliance,
      color: '#2f5fb0',
      asLine: true,
      useRightAxis: true,
      showPointLabels: true,
      pointLabelSuffix: '%',
    },
  ];
  const rows = items.map((y, i) => ({
    period: y.academic_year,
    expected,
    submitted: submitted[i],
    pending: pending[i],
    compliance: compliance[i],
    compliancePct: `${compliance[i]}%`,
  }));
  return { labels, series, rows };
}

// Year-wise data-entry compliance (schoolinfrasummary.academic_years) ->
// combo chart (submitted/pending bars + compliance % line) and table rows.
function buildComplianceFromYears(years: SchoolInfraSummaryYear[]) {
  const labels = years.map((y) => y.academic_year);
  const submitted = years.map((y) => iNum(y.submitted));
  const pending = years.map((y) => iNum(y.pending));
  const compliance = years.map((y) => Number(y.compliance_pct) || 0);
  const series: ChartSeries[] = [
    {
      label: 'Submitted',
      data: submitted,
      color: SUBMITTED_COLOR,
      barColors: fadeExceptLast(SUBMITTED_COLOR, labels.length),
    },
    {
      label: 'Pending',
      data: pending,
      color: PENDING_COLOR,
      barColors: fadeExceptLast(PENDING_COLOR, labels.length),
    },
    {
      label: 'Compliance %',
      data: compliance,
      color: '#2f5fb0',
      asLine: true,
      useRightAxis: true,
      showPointLabels: true,
      pointLabelSuffix: '%',
    },
  ];
  const rows = years.map((y, i) => ({
    period: y.academic_year,
    expected: iNum(y.expected),
    submitted: submitted[i],
    pending: pending[i],
    compliance: compliance[i],
    compliancePct: `${compliance[i]}%`,
  }));
  return { labels, series, rows };
}

/** Classification filters shared from the Dashboard header (id strings). */
export interface InfrastructureProps {
  /** Active sub-tab from the dashboard header (numbers | comparison | …). */
  activeSubTab?: string;
  /** Global academic year id from the dashboard header (defaults to latest). */
  acdYr?: number | string | null;
  /** Header-selected district id → district-level summary. */
  districtId?: number | string | null;
  /** Header-selected block id → block-level summary. */
  blockId?: number | string | null;
  /** Header-selected district / block names (for breadcrumb + sync). */
  district?: string | null;
  block?: string | null;
  /** Header-selected school name; Infrastructure resolves its own school id
   * and UDISE from studentchildcountallyear before issuing scoped requests. */
  school?: string | null;
  /** UDISE from the selected header school; schoolinfrasummary is keyed by it. */
  schoolUdise?: string | null;
  /** Header setters so the Comparison drill syncs the top dropdowns. */
  onDistrictChange?: (value: string) => void;
  onBlockChange?: (value: string) => void;
  onSchoolChange?: (value: string) => void;
  /**
   * Focus the dashboard on one school, setting District + Block + School
   * together. Used by the Caution tab's flagged-school row click, whose rows
   * are already at school level and so cannot drill any deeper.
   */
  onGeoSelect?: (geo: {
    district?: string | null;
    block?: string | null;
    school?: string | null;
  }) => void;
  /**
   * Report the Block / School dropdown options the HEADER should show while
   * the Caution sub-tab is active: only the blocks/schools that actually
   * have flagged schools for the selected check, in sync with the other
   * tabs' Caution view. Reverts to the normal full lists once Caution
   * unmounts (sub-tab switch away) — see InfrastructureCaution.
   */
  onScopeOptions?: (
    opts: {
      blocks: { label: string; value: string }[];
      schools: { label: string; value: string }[];
    } | null
  ) => void;
  /** Classification filters from the dashboard header (id strings). */
  filters?: Filters;
}

/**
 * When a school is selected in the header, narrow an entity array (the schools
 * of the block the drill returned) to just that one school, so the chart/table
 * shows a single entity in sync with the selection. Matches on the bare name
 * (strips any trailing " (udise)"). Falls back to the full list on no match.
 */
const narrowToSchool = <T,>(
  rows: T[],
  name: (r: T) => unknown,
  school?: string | null
): T[] => {
  if (!school) return rows;
  const want = school.trim().toLowerCase();
  const strip = (s: string) =>
    s
      .replace(/\s*\(\d[\d/]*\)\s*$/, '')
      .trim()
      .toLowerCase();
  const filtered = rows.filter((r) => strip(String(name(r) ?? '')) === want);
  return filtered.length > 0 ? filtered : rows;
};

/**
 * Infrastructure dashboard (Infrastructure nav tab).
 *
 * Owns the left sidebar (type / month / source) and swaps the main content by
 * sub-tab: "numbers" (KPIs, charts, tables) or "comparison" (ranked districts).
 * Reuses AppChart (doughnut / combo / bar) and ReportDataTable. All figures are
 * mock data from shared/json/infrastructureData.ts — swap for an API later.
 */
const Infrastructure: React.FC<InfrastructureProps> = ({
  activeSubTab = 'numbers',
  acdYr = null,
  districtId = null,
  blockId = null,
  district = null,
  block = null,
  school = null,
  schoolUdise = null,
  onDistrictChange,
  onBlockChange,
  onSchoolChange,
  onGeoSelect,
  onScopeOptions,
  filters = EMPTY_FILTERS,
}) => {
  const [activeType, setActiveType] = useState(infraTypes[0].key);
  const [enteredMonth, setEnteredMonth] = useState('september-2026-27');
  const [source, setSource] = useState<'emis' | 'udise'>('emis');

  const activeMeta = infraTypes.find((t) => t.key === activeType)!;
  // ── Infrastructure summary API (schoolinfrasummary) ──
  // Infrastructure is frozen to the 2025-2026 academic year (no selection);
  // resolve its acd_yr id from commonmasters by the start year.
  const { data: commonMasters } = useGetCommonMastersQuery();
  const frozenAcdYr = useMemo(() => {
    const rows = commonMasters?.result?.academic_year ?? [];
    const row = rows.find((y) => String(y?.name ?? '').startsWith('2025'));
    return row?.id != null ? Number(row.id) : null;
  }, [commonMasters]);

  // Resolve the selected school in the ID space used by the infrastructure
  // tables. The header dropdown's schoollist `id` is a different identifier;
  // studentchildcountallyear carries the same `school_id` as the infrastructure
  // tables plus the UDISE required by schoolinfrasummary.
  const identityAcdYr = acdYr != null ? Number(acdYr) : (frozenAcdYr ?? null);
  const { data: infraSchoolRoster, isFetching: infraSchoolIdentityLoading } =
    useGetStudentChildCountAllYearQuery(
      {
        acd_yr: identityAcdYr as number,
        district_id: Number(districtId),
      },
      {
        skip: identityAcdYr == null || districtId == null || !school,
      }
    );

  const infraSchoolIdentity = useMemo(() => {
    if (!school) return null;
    const normalize = (value: unknown) =>
      String(value ?? '')
        .replace(/\s*\(\d[\d/]*\)\s*$/, '')
        .trim()
        .toLowerCase();
    const rows = infraSchoolRoster?.result ?? [];
    const scoped =
      blockId == null
        ? rows
        : rows.filter((row) => Number(row.block_id) === Number(blockId));
    const row = scoped.find(
      (candidate) => normalize(candidate.school_name) === normalize(school)
    );
    if (!row) return null;
    const schoolId = Number(row.school_id ?? 0);
    const udiseCode = String(row.udise_code ?? '').trim();
    return schoolId > 0 ? { schoolId, udiseCode } : null;
  }, [school, blockId, infraSchoolRoster]);

  const resolvedInfraSchoolId = infraSchoolIdentity?.schoolId ?? null;
  const resolvedInfraSchoolUdise =
    String(schoolUdise ?? '').trim() || infraSchoolIdentity?.udiseCode || '';
  const waitingForInfraSchoolId =
    !!school && (infraSchoolIdentityLoading || resolvedInfraSchoolId == null);

  // One request per infrastructure type, scoped by the selected geography.
  // At school scope send BOTH resolved identifiers: school_id for deployments
  // that support it directly, and UDISE for the summary table keyed by UDISE.
  // Do not make UDISE mandatory — once the school ID resolves this request must
  // run alongside the other infrastructure requests.
  //
  // All three queries below (infraSummary / typeDistricts / acdWise) feed ONLY
  // the Numbers sub-tab (KPI tiles, charts, overview table) — Comparison and
  // Caution render their own child components with their own queries. Without
  // the activeSubTab skip, switching to Comparison/Caution while changing
  // district/block/filters kept refetching these three Numbers-only queries
  // alongside whichever sub-tab's own query was actually needed, delaying it.
  const isNumbersTab = activeSubTab === 'numbers';
  const typeId = UI_TYPE_ID[activeType] ?? 1;
  const { data: infraSummary, isFetching: infraSummaryFetching } =
    useGetSchoolInfraSummaryQuery(
      {
        type: typeId,
        ...(districtId != null && { district_id: Number(districtId) }),
        ...(blockId != null && { block_id: Number(blockId) }),
        ...(resolvedInfraSchoolId != null && {
          school_id: resolvedInfraSchoolId,
        }),
        ...(resolvedInfraSchoolUdise && {
          udise_code: resolvedInfraSchoolUdise,
        }),
        ...(filters.management[0] ? { manage_id: filters.management[0] } : {}),
        ...(filters.schoolType[0]
          ? { school_type_id: filters.schoolType[0] }
          : {}),
        ...(filters.category[0] ? { cate_id: filters.category[0] } : {}),
      },
      { skip: !isNumbersTab || frozenAcdYr == null || waitingForInfraSchoolId }
    );
  const infraLoading = infraSummaryFetching || waitingForInfraSchoolId;
  const summaryResult = infraSummary?.result;

  // Per-type district breakdown table. Its numeric school_id is resolved from
  // the shared child-count roster above, never from schoollist.id.
  const { data: typeDistricts, isFetching: typeDistrictsFetching } =
    useGetSchoolInfraTypeDistrictsQuery(
      {
        type: typeId,
        ...(districtId != null && { district_id: Number(districtId) }),
        ...(blockId != null && { block_id: Number(blockId) }),
        ...(resolvedInfraSchoolId != null && {
          school_id: resolvedInfraSchoolId,
        }),
        ...(filters.management[0] ? { manage_id: filters.management[0] } : {}),
        ...(filters.schoolType[0]
          ? { school_type_id: filters.schoolType[0] }
          : {}),
        ...(filters.category[0] ? { cate_id: filters.category[0] } : {}),
      },
      { skip: !isNumbersTab || frozenAcdYr == null || waitingForInfraSchoolId }
    );
  const districtsLoading = typeDistrictsFetching || waitingForInfraSchoolId;
  //console.log(summaryResult, 'summaryResult');
  //console.log(typeDistricts, 'typeDistricts');
  const detail = useMemo(
    () =>
      summaryResult
        ? buildDetailFromSummary(summaryResult)
        : school
          ? emptyDetail(infraDetails[activeType])
          : infraDetails[activeType],
    [summaryResult, activeType, school]
  );

  const overviewRows = useMemo(() => {
    const rows = summaryResult?.overview?.length
      ? buildOverviewFromSummary(summaryResult.overview)
      : [];
    return rows.length ? rows : school ? [] : infraOverview;
  }, [summaryResult, school]);

  // Academic-year-wise submissions for the Data Entry Compliance by Period
  // chart + table. Uses the resolved infrastructure school_id at school scope.
  const { data: acdWise, isFetching: acdWiseFetching } =
    useGetInfraAcdWiseSummaryQuery(
      {
        ...(districtId != null && { district_id: Number(districtId) }),
        ...(blockId != null && { block_id: Number(blockId) }),
        ...(resolvedInfraSchoolId != null && {
          school_id: resolvedInfraSchoolId,
        }),
        ...(filters.management[0] ? { manage_id: filters.management[0] } : {}),
        ...(filters.schoolType[0]
          ? { school_type_id: filters.schoolType[0] }
          : {}),
        ...(filters.category[0] ? { cate_id: filters.category[0] } : {}),
      },
      { skip: !isNumbersTab || frozenAcdYr == null || waitingForInfraSchoolId }
    );
  const acdWiseLoading = acdWiseFetching || waitingForInfraSchoolId;

  // SCHOOLS EXPECTED from schoolinfrasummary — the denominator for pending and
  // compliance %, so both tiles and this chart quote the same base.
  const schoolsExpected = iNum(summaryResult?.counts?.schools_expected);

  const apiCompliance = useMemo(() => {
    // Prefer infraAcdWiseSummary (academic-year-wise) once it has rows AND we
    // know the expected base to derive pending / compliance % against.
    const items = acdWise?.result?.items;
    if (items?.length && schoolsExpected > 0) {
      return buildComplianceFromAcdWise(items, schoolsExpected);
    }
    // Fall back to schoolinfrasummary's own academic_years block, which
    // already carries expected / pending / compliance_pct per year.
    return summaryResult?.academic_years?.length
      ? buildComplianceFromYears(summaryResult.academic_years)
      : null;
  }, [acdWise, schoolsExpected, summaryResult]);
  const isCaution = activeSubTab === 'caution';

  const monthOptions = [
    { label: 'September 2026-27', value: 'september-2026-27' },
    { label: 'August 2026-27', value: 'august-2026-27' },
    { label: 'July 2026-27', value: 'july-2026-27' },
    { label: 'June 2026-27', value: 'june-2026-27' },
  ];

  const complianceLabels =
    apiCompliance?.labels ??
    (school ? [] : infraComplianceByPeriod.map((c) => c.period));
  const complianceSeries =
    apiCompliance?.series ?? (school ? [] : buildComplianceSeries());
  const overviewLabels = overviewRows.map((r) => r.type);
  const overviewSeries: ChartSeries[] = [
    {
      label: 'Available',
      data: overviewRows.map((r) => r.available),
      color: '#8bc34a',
    },
    {
      label: 'Shortfall',
      data: overviewRows.map((r) => r.shortfall),
      color: '#c0392b',
    },
  ];
  // Rich hover tooltip for the overview bar chart, keyed by infrastructure
  // type. The default per-series tooltip only shows Available / Shortfall; this
  // adds Required (shortfall), Surplus, Norms and Availability.
  const overviewTooltip: Record<string, { label: string; value: string }[]> =
    {};
  overviewRows.forEach((r) => {
    const type = String(r.type ?? '');
    if (!type) return;
    overviewTooltip[type] = [
      { label: 'Available', value: iFmt(r.available) },
      { label: 'Required (shortfall)', value: iFmt(r.shortfall) },
      { label: 'Surplus', value: iFmt(r.surplus) },
      { label: 'Norms', value: iFmt(r.norms) },
      { label: 'Availability', value: String(r.availability ?? '') },
    ];
  });

  const highlightPeriod = complianceLabels[complianceLabels.length - 1];

  // ── Compliance-by-period table ──
  const complianceColumns: ReportColumn[] = [
    { key: 'period', label: 'Academic Year', type: 'text' },
    { key: 'expected', label: 'Expected', type: 'number', align: 'left' },
    {
      key: 'submitted',
      label: 'Submitted',
      type: 'number',
      align: 'left',
      textColor: '#2e8b48',
    },
    {
      key: 'pending',
      label: 'Pending',
      type: 'number',
      align: 'left',
      textColor: '#d9534f',
    },
    { key: 'compliancePct', label: 'Compliance', type: 'text', align: 'left' },
  ];
  const complianceTableData =
    apiCompliance?.rows ??
    (school
      ? []
      : infraComplianceByPeriod.map((c) => ({
          ...c,
          compliancePct: `${c.compliance}%`,
        })));

  // Rich hover tooltip for the compliance combo chart, keyed by period label.
  // The default per-series tooltip only lists the plotted series (Submitted /
  // Pending / Compliance %); this adds Expected and keeps a consistent order.
  const nf = (v: unknown) => Number(v ?? 0).toLocaleString('en-IN');
  const complianceTooltip: Record<string, { label: string; value: string }[]> =
    {};
  (complianceTableData as Array<Record<string, unknown>>).forEach((r) => {
    const period = String(r.period ?? '');
    if (!period) return;
    complianceTooltip[period] = [
      { label: 'Submitted', value: nf(r.submitted) },
      { label: 'Pending', value: nf(r.pending) },
      { label: 'Expected', value: nf(r.expected) },
      { label: 'Compliance', value: `${Number(r.compliance ?? 0)}%` },
    ];
  });

  // ── Overview table (all infrastructure types) ──
  const overviewColumns: ReportColumn[] = [
    { key: 'type', label: 'Type', type: 'text' },
    { key: 'available', label: 'Available', type: 'number' },
    { key: 'norms', label: 'Norms', type: 'number' },
    {
      key: 'shortfall',
      label: 'Shortfall',
      type: 'number',
      textColor: '#dc2626',
    },
    {
      key: 'surplus',
      label: 'Surplus',
      type: 'number',
      textColor: '#16a34a',
    },
    {
      key: 'availability',
      label: 'Availability',
      type: 'text',
      highlightExtremes: 'both',
    },
    {
      key: 'coverage',
      label: 'Coverage',
      type: 'text',
      highlightExtremes: 'min',
    },
    { key: 'good', label: 'Good', type: 'text', highlightExtremes: 'min' },
  ];

  // ── District drill-down table (per type, schoolinfratypedistricts) ──
  // The API scopes this same endpoint by geography: no district/block
  // selected → district rows; a district selected → that district's blocks;
  // a block selected → that block's schools. `result.level` tells us which
  // one came back so the table's title/columns/click behavior can adapt.
  const typeLevel = (typeDistricts?.result?.level ??
    (blockId != null
      ? 'school'
      : districtId != null
        ? 'block'
        : 'district')) as 'district' | 'block' | 'school';
  // const canDrillDistrictTable = typeLevel !== 'school';
  const districtLevelNoun =
    typeLevel === 'school'
      ? 'Schools'
      : typeLevel === 'block'
        ? 'Blocks'
        : 'Districts';
  const districtTableTitle = `${activeMeta.label} by ${districtLevelNoun}${
    district ? ` — ${district}${block ? `, ${block}` : ''}` : ''
  }`;

  const districtRows = useMemo(() => {
    const rows = typeDistricts?.result?.items ?? [];
    if (!rows.length) return school ? [] : getInfraDistricts(activeType);
    const mapped = rows.map((r) => ({
      name: String(r.name ?? r.district_name ?? ''),
      available: iNum(r.available),
      norms: iNum(r.norms ?? r.required),
      shortfall: iNum(r.shortfall),
      availability: iPct(r.availability_pct),
      coverage: iPct(r.coverage_pct),
      good: iPct(r.good_pct),
      gap: iPct(r.gap_pct),
    }));
    // A selected school → just that one school's row (in sync with the header).
    return typeLevel === 'school'
      ? narrowToSchool(mapped, (r) => r.name, school)
      : mapped;
  }, [typeDistricts, activeType, typeLevel, school]);
  const districtColumns: ReportColumn[] = [
    {
      // District/block rows drill one level deeper (link + chevron); schools
      // are the leaf and are plain text — there is no per-school infra view.
      key: 'name',
      label: drillLevelLabel(typeLevel),
      type: 'link',
    },
    { key: 'available', label: 'Available', type: 'number' },
    { key: 'norms', label: 'Norms', type: 'number' },
    {
      key: 'shortfall',
      label: 'Shortfall',
      type: 'number',
      textColor: '#dc2626',
    },
    { key: 'availability', label: 'Availability', type: 'text' },
    { key: 'good', label: 'Good', type: 'text' },
    { key: 'gap', label: 'Gap', type: 'text' },
  ];
  console.log(districtColumns, 'districtColumns');
  // Current geography scope for the headings on this tab, so a title never
  // claims "Tamil Nadu" while the figures are for one district or block.
  const infraScopeName = scopeLabel({ district, block, school });

  // Row click on the district/block table drills one level deeper: a
  // district row sets the header district (→ its blocks), a block row sets
  // the header block (→ its schools), keeping the top filter dropdowns in
  // sync (same pattern as the Comparison sub-tab's bar-click drill).
  const handleDistrictTableRowClick = (row: Record<string, unknown>) => {
    const name = String(row.name ?? '');
    if (!name) return;
    if (typeLevel === 'district') onDistrictChange?.(name);
    else if (typeLevel === 'block') onBlockChange?.(name);
    else onSchoolChange?.(name);
    // School level is the leaf — no deeper drill and no per-school view.
  };

  return (
    <div className={`infra${isCaution ? ' infra--no-sidebar' : ''}`}>
      {/* Left sidebar (type + month + source). Hidden on the Caution tab, whose
      checks span all types and don't use these controls. */}
      {!isCaution && (
        <aside className="infra__sidebar">
          <div className="infra__side-group">
            <p className="infra__side-heading">
              <i className="pi pi-sitemap" /> Infrastructure Type
            </p>
            <div className="infra__type-list">
              {infraTypes.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`infra__type${
                    activeType === t.key ? ' is-active' : ''
                  }`}
                  onClick={() => setActiveType(t.key)}
                >
                  <AppIcon icon={t.icon} />
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          </div>
        </aside>
      )}

      {/* ── Main content ── */}
      <div className="infra__main">
        {/* ── Comparison sub-tab ── */}
        {activeSubTab === 'comparison' && (
          <InfrastructureComparison
            activeType={activeType}
            source={source}
            type={typeId}
            acdYr={frozenAcdYr}
            filters={filters}
            districtId={districtId != null ? Number(districtId) : null}
            blockId={blockId != null ? Number(blockId) : null}
            district={district}
            block={block}
            school={school}
            schoolId={resolvedInfraSchoolId}
            onDistrictChange={onDistrictChange}
            onBlockChange={onBlockChange}
            onSchoolChange={onSchoolChange}
          />
        )}

        {/* ── Alarming sub-tab ── */}
        {/* {activeSubTab === 'alarming' && (
          <InfrastructureAlarming
            activeType={activeType}
            source={source}
            district={district}
            block={block}
            onDistrictChange={onDistrictChange}
            onBlockChange={onBlockChange}
          />
        )} */}

        {/* ── Caution sub-tab ── */}
        {activeSubTab === 'caution' && (
          <InfrastructureCaution
            source={source}
            acdYr={frozenAcdYr}
            districtId={districtId}
            blockId={blockId}
            district={district}
            block={block}
            school={school}
            schoolId={resolvedInfraSchoolId}
            schoolUdise={resolvedInfraSchoolUdise}
            onDistrictChange={onDistrictChange}
            onBlockChange={onBlockChange}
            onSchoolChange={onSchoolChange}
            onGeoSelect={onGeoSelect}
            onScopeOptions={onScopeOptions}
            filters={filters}
          />
        )}

        {/* ── Numbers sub-tab (KPIs, charts, tables) ── */}
        {activeSubTab === 'numbers' && (
          <>
            {/* Title */}
            <div className="infra__title-row">
              <AppIcon className="infra__title-icon" icon={activeMeta.icon} />
              <h5 className="infra__title">
                {activeMeta.label} — {infraScopeName}
                <span className="infra__title-sub">
                  {' '}
                  {source === 'emis' ? 'EMIS' : 'UDISE+'} ·{' '}
                  {monthOptions.find((m) => m.value === enteredMonth)?.label}
                </span>
              </h5>
            </div>

            {/* Breadcrumb / back — shown once the "by Districts" table below
            has been drilled into a district (→ its blocks) or block (→ its
            schools), mirroring the Comparison sub-tab's drill pattern. */}
            <DrillBreadcrumb
              district={district}
              block={block}
              onDistrictChange={onDistrictChange}
              onBlockChange={onBlockChange}
              onSchoolChange={onSchoolChange}
            />

            {/* KPI tiles */}
            <div className="infra__kpis">
              {detail.kpis.map((k) => (
                <div key={k.label} className={`infra-kpi infra-kpi--${k.tone}`}>
                  <span className="infra-kpi__icon">
                    {/* Each KPI's OWN icon (clipboard-check / scale-balanced /
                        circle-check / triangle-exclamation). This used to
                        render activeMeta.icon, which repeated the one
                        infrastructure-type glyph across all four tiles and
                        told the user nothing about the metric. Falls back to
                        the type icon only if a KPI omits its own. */}
                    <AppIcon icon={k.icon || activeMeta.icon} />
                  </span>
                  <div className="infra-kpi__body">
                    {infraLoading ? (
                      <Skeleton width="60%" height="1.4rem" />
                    ) : (
                      <span className="infra-kpi__value">{k.value}</span>
                    )}
                    <span className="infra-kpi__label">{k.label}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Secondary stat tiles */}
            <div className="infra__stats">
              {detail.stats.map((s) => (
                <div key={s.label} className="infra-stat">
                  {infraLoading ? (
                    <Skeleton width="50%" height="1.1rem" />
                  ) : (
                    <span className="infra-stat__value">{s.value}</span>
                  )}
                  <span className="infra-stat__label">{s.label}</span>
                </div>
              ))}
            </div>

            {/* Data entry doughnut + compliance combo */}
            <div className="infra__grid infra__grid--2">
              <div className="infra__card">
                <AppChart
                  type="doughnut"
                  title={`Data Entry — ${
                    monthOptions.find((m) => m.value === enteredMonth)?.label
                  }`}
                  titleSuffix={detail.dataEntrySubtitle}
                  icon="fa-solid fa-address-card"
                  iconColor="#2f5fb0"
                  slices={detail.dataEntry}
                  loading={infraLoading}
                  height={320}
                />
              </div>
              <div className="infra__card">
                <AppChart
                  // Stacked, not grouped: Submitted + Pending add up to
                  // Expected, so one bar per year reads as "how much of the
                  // expected roll came in". The Compliance % line is on the
                  // right axis and so is unaffected by the stacking.
                  type="stackedBar"
                  title={`Data Entry Compliance by Year — ${infraScopeName}`}
                  showCheck
                  titleSuffix={`${highlightPeriod} highlighted`}
                  tools={[
                    { icon: 'pi pi-refresh', label: 'Refresh' },
                    { icon: 'pi pi-file', label: 'Table View' },
                    { icon: 'pi pi-chart-bar', label: 'Bar view' },
                    { icon: 'pi pi-chart-line', label: 'Line view' },
                    { icon: 'pi pi-download', label: 'Export' },
                  ]}
                  labels={complianceLabels}
                  series={complianceSeries}
                  tooltipDetails={complianceTooltip}
                  loading={infraLoading || acdWiseLoading}
                  yTitle="Schools"
                  y1Title="Compliance %"
                  y1TickSuffix="%"
                  // No `highlightLabel` here: the per-series `barColors` built
                  // by fadeExceptLast already mark the latest year, and
                  // barColors takes precedence over highlightLabel anyway.
                  height={320}
                />
              </div>
            </div>

            {/* Compliance-by-period table */}
            <div className="infra__block">
              <ReportDataTable
                title={`Data Entry Compliance by Year — ${infraScopeName}`}
                icon="pi pi-table"
                data={complianceTableData}
                loading={infraLoading || acdWiseLoading}
                columns={complianceColumns}
                exportFileName={`${activeType}-compliance-by-period`}
                searchable={false}
                equalColumns
                rows={12}
              />
            </div>

            {/* vs Norms + Condition doughnuts */}
            <div className="infra__grid infra__grid--2">
              <div className="infra__card">
                <AppChart
                  type="doughnut"
                  title={`${activeMeta.label} vs Norms`}
                  icon="fa-solid fa-scale-balanced"
                  iconColor="#3b82f6"
                  slices={detail.vsNorms}
                  loading={infraLoading}
                  height={320}
                />
              </div>
              <div className="infra__card">
                <AppChart
                  type="doughnut"
                  title={`${activeMeta.label} Condition`}
                  icon="fa-solid fa-gears"
                  iconColor="#7e57c2"
                  slices={detail.condition}
                  loading={infraLoading}
                  height={320}
                />
              </div>
            </div>

            {/* All infrastructure types — available vs shortfall */}
            <div className="infra__block">
              <AppChart
                type="bar"
                title="All Infrastructure Types — Available vs Shortfall"
                titleSuffix="pick a type in the sidebar to focus"
                icon="fa-solid fa-chart-column"
                iconColor="#6d4bd8"
                labels={overviewLabels}
                series={overviewSeries}
                tooltipDetails={overviewTooltip}
                loading={infraLoading}
                yTitle="Units"
                yTickSuffix=""
                height={300}
              />
            </div>

            {/* Infrastructure overview table (all types) */}
            <div className="infra__block">
              <ReportDataTable
                title={`Infrastructure Overview — ${infraScopeName}`}
                hint="(click a row to drill down)"
                icon="fa-solid fa-chart-column"
                data={overviewRows as unknown as Record<string, unknown>[]}
                loading={infraLoading}
                columns={overviewColumns}
                exportFileName="infrastructure-overview"
                searchable={false}
                rows={12}
                equalColumns
                onRowClick={(row) => setActiveType(String(row.key))}
              />
            </div>

            {/* Per-type district drill-down table */}
            <div className="infra__block">
              {/* Repeated next to the grid that drills: the section's other
                  breadcrumb is above the KPI tiles and charts, too far to
                  reach after clicking a row down here. */}
              <DrillBreadcrumb
                district={district}
                block={block}
                onDistrictChange={onDistrictChange}
                onBlockChange={onBlockChange}
                onSchoolChange={onSchoolChange}
              />
              <ReportDataTable
                title={districtTableTitle}
                hint={drillHint(typeLevel)}
                icon="fa-solid fa-id-card"
                data={districtRows as unknown as Record<string, unknown>[]}
                loading={districtsLoading}
                // columns={districtColumns}
                columns={
                  typeLevel === 'school' && districtRows.length === 1
                    ? [
                        { ...districtColumns[0], type: 'text' },
                        ...districtColumns.slice(1),
                      ]
                    : districtColumns
                }
                exportFileName={`${activeType}-by-${districtLevelNoun.toLowerCase()}`}
                searchPlaceholder={drillSearchPlaceholder(typeLevel)}
                rows={12}
                equalColumns
                onRowClick={handleDistrictTableRowClick}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Infrastructure;
